/**
 * Centralized Route-to-Title Mapping
 * 
 * Maps application routes to their corresponding page titles.
 * Used by useDocumentTitle hook to automatically update browser tab titles.
 * 
 * Priority:
 * 1. Exact route match
 * 2. Route prefix match (for nested routes)
 * 3. Fallback to formatted route name
 */

import { BRANDING } from './branding';
import { COMPANY_BRANDING } from './companyBranding';

/**
 * Route to page title mapping
 * Keys should match route paths (with or without /onecare/ prefix)
 */
export const ROUTE_TITLES: Record<string, string> = {
  // Public/Landing pages — root is the Aimepic company page, not OneCare
  '/': COMPANY_BRANDING.meta.defaultTitle,
  '/onecare/': BRANDING.pageTitles.landing,
  '/onecare/login': BRANDING.pageTitles.login,
  '/onecare/login-legacy': BRANDING.pageTitles.loginLegacy,
  
  // Main authenticated routes
  '/dashboard': BRANDING.pageTitles.dashboard,
  '/onecare/dashboard': BRANDING.pageTitles.dashboard,
  '/communicator': BRANDING.pageTitles.communicator,
  '/onecare/messages': BRANDING.pageTitles.messages,
  '/onecare/communicator': BRANDING.pageTitles.communicator,
  '/onecare/communicator/conversations': BRANDING.pageTitles.messages,
  '/tasks': 'Tasks - OneCare',
  '/onecare/tasks': 'Tasks - OneCare',
  '/ai-chat': BRANDING.pageTitles.aiChat,
  '/onecare/ai-chat': BRANDING.pageTitles.aiChat,
  '/notifications': BRANDING.pageTitles.notifications,
  '/onecare/notifications': BRANDING.pageTitles.notifications,
  '/settings': BRANDING.pageTitles.settings,
  '/onecare/settings': BRANDING.pageTitles.settings,
  
  // Tool routes
  '/tools/conductor': 'Conductor - OneCare',
  '/onecare/tools/conductor': 'Conductor - OneCare',
  '/tools/smart-flow': 'Smart Flow - OneCare',
  '/onecare/tools/smart-flow': 'Smart Flow - OneCare',
  '/tools/shift-scheduling': 'Shift Scheduling - OneCare',
  '/onecare/tools/shift-scheduling': 'Shift Scheduling - OneCare',
  '/onecare/admin/shift-scheduling-policy': 'Shift Scheduling Policy - OneCare',
  '/tools/data-analysis': 'Data Analysis - OneCare',
  '/onecare/tools/data-analysis': 'Data Analysis - OneCare',
  
  // Staff routes
  '/staff/patients': BRANDING.pageTitles.patients,
  '/onecare/staff/patients': BRANDING.pageTitles.patients,
  '/staff/feedback': 'Patient Feedback - OneCare',
  '/onecare/staff/feedback': 'Patient Feedback - OneCare',
  '/staff/dashboard': BRANDING.pageTitles.staff,
  '/onecare/staff/dashboard': BRANDING.pageTitles.staff,
  '/staff': 'Staff Directory - OneCare',
  '/onecare/staff': 'Staff Directory - OneCare',
  '/appointments': 'Appointments - OneCare',
  '/onecare/appointments': 'Appointments - OneCare',
  '/reports': 'Reports - OneCare',
  '/onecare/reports': 'Reports - OneCare',
  
  // Patient routes
  '/onecare/patients': 'Patient Portal - OneCare',
  '/onecare/patients/ai-chat': 'AI Assistant - OneCare',
  '/onecare/patients/services': 'Services - OneCare',
  '/onecare/patients/medical-assistance': 'Medical Assistance - OneCare',
  '/onecare/patients/encounter': 'Encounter - OneCare',
  '/onecare/patients/encounter-info': 'Encounter Information - OneCare',
  '/onecare/patients/feedback': 'Patient Feedback - OneCare',
  '/onecare/patients/track': 'Track Feedback - OneCare',
  '/onecare/patients/feedback/track': 'Track Feedback - OneCare',
  
  // Platform pages
  '/onecare/platform/messaging': 'Messaging Platform - OneCare',
  '/onecare/platform/conductor': 'Conductor Platform - OneCare',
  '/onecare/platform/smart-flow': 'Smart Flow Platform - OneCare',
  '/onecare/platform/patient-feedback': 'Patient Feedback Platform - OneCare',
  
  // Admin routes - main
  '/admin': BRANDING.pageTitles.admin,
  '/onecare/admin': BRANDING.pageTitles.admin,
  '/onecare/admin/platform': BRANDING.pageTitles.platformAdmin,
  
  // Admin routes - specific pages
  '/admin/global-overview': 'Global Overview - OneCare',
  '/onecare/admin/global-overview': 'Global Overview - OneCare',
  '/admin/users': 'User Management - OneCare',
  '/onecare/admin/users': 'User Management - OneCare',
  '/admin/system-health': 'System Health - OneCare',
  '/onecare/admin/system-health': 'System Health - OneCare',
  '/admin/security': 'Security - OneCare',
  '/onecare/admin/security': 'Security - OneCare',
  '/admin/hospitals': 'Hospital Management - OneCare',
  '/onecare/admin/hospitals': 'Hospital Management - OneCare',
  '/admin/setup': 'Setup Wizard - OneCare',
  '/onecare/admin/setup': 'Setup Wizard - OneCare',
  '/admin/organizations': 'Organizations - OneCare',
  '/onecare/admin/organizations': 'Organizations - OneCare',
  '/admin/hospitals-multi': 'Hospitals & Facilities - OneCare',
  '/onecare/admin/hospitals-multi': 'Hospitals & Facilities - OneCare',
  '/admin/alerts': 'Alerts - OneCare',
  '/onecare/admin/alerts': 'Alerts - OneCare',
  '/admin/settings': 'System Settings - OneCare',
  '/onecare/admin/settings': 'System Settings - OneCare',
  '/admin/theme-control': 'Theme Control - OneCare',
  '/onecare/admin/theme-control': 'Theme Control - OneCare',
  '/admin/ai-permissions': 'AI Permissions - OneCare',
  '/onecare/admin/ai-permissions': 'AI Permissions - OneCare',
  '/admin/urgency-permissions': 'Urgency Permissions - OneCare',
  '/onecare/admin/urgency-permissions': 'Urgency Permissions - OneCare',
  '/admin/messaging-channels': 'Messaging Channels - OneCare',
  '/onecare/admin/messaging-channels': 'Messaging Channels - OneCare',
  '/admin/messaging-permissions/superuser': 'Messaging Permissions - OneCare',
  '/onecare/admin/messaging-permissions/superuser': 'Messaging Permissions - OneCare',
  '/admin/messaging-permissions/org-admin': 'Messaging Permissions - OneCare',
  '/onecare/admin/messaging-permissions/org-admin': 'Messaging Permissions - OneCare',
  '/admin/messaging-permissions/domain-admin': 'Messaging Permissions - OneCare',
  '/onecare/admin/messaging-permissions/domain-admin': 'Messaging Permissions - OneCare',
  '/admin/feature-access/organization': 'Organization Feature Access - OneCare',
  '/onecare/admin/feature-access/organization': 'Organization Feature Access - OneCare',
  '/admin/feature-access/domain': 'User Feature Access - OneCare',
  '/onecare/admin/feature-access/domain': 'User Feature Access - OneCare',
  '/admin/notification-triggers': 'Notification Triggers - OneCare',
  '/onecare/admin/notification-triggers': 'Notification Triggers - OneCare',
  '/admin/patient-experience': 'Patient Experience - OneCare',
  '/onecare/admin/patient-experience': 'Patient Experience - OneCare',
  '/admin/patient-feedback': 'Patient Feedback - OneCare',
  '/onecare/admin/patient-feedback': 'Patient Feedback - OneCare',
  '/admin/duty-schedules': 'Duty Schedules - OneCare',
  '/onecare/admin/duty-schedules': 'Duty Schedules - OneCare',
  '/admin/feedback-management': 'Feedback Management - OneCare',
  '/onecare/admin/feedback-management': 'Feedback Management - OneCare',
  '/admin/notification-sounds': 'Notification Sounds - OneCare',
  '/onecare/admin/notification-sounds': 'Notification Sounds - OneCare',
  '/admin/conductor-analytics': 'Conductor Analytics - OneCare',
  '/onecare/admin/conductor-analytics': 'Conductor Analytics - OneCare',
  '/admin/conductor-settings': 'Conductor Settings - OneCare',
  '/onecare/admin/conductor-settings': 'Conductor Settings - OneCare',
  '/admin/ai-management': 'AI Management - OneCare',
  '/onecare/admin/ai-management': 'AI Management - OneCare',
  '/admin/hospital-conductor-balances': 'Hospital Conductor Balances - OneCare',
  '/onecare/admin/hospital-conductor-balances': 'Hospital Conductor Balances - OneCare',
  '/admin/audit-logs': 'Audit Logs - OneCare',
  '/onecare/admin/audit-logs': 'Audit Logs - OneCare',
  
  // Admin nested routes (messaging channels)
  '/admin/messaging-channels/new': 'New Messaging Channel - OneCare',
  '/onecare/admin/messaging-channels/new': 'New Messaging Channel - OneCare',
  '/admin/messaging-channels/bulk': 'Bulk Messaging Channels - OneCare',
  '/onecare/admin/messaging-channels/bulk': 'Bulk Messaging Channels - OneCare',
};

/**
 * Get page title for a given route path
 * @param pathname - Current route pathname
 * @returns Page title string
 */
export const getRouteTitle = (pathname: string): string => {
  // Normalize pathname (remove trailing slash except for root)
  const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
  
  // Check exact match first
  if (ROUTE_TITLES[normalizedPath]) {
    return ROUTE_TITLES[normalizedPath];
  }
  
  // Check for admin routes (nested routes) - handle both /admin/ and /onecare/admin/
  if (normalizedPath.startsWith('/admin/') || normalizedPath.startsWith('/onecare/admin/')) {
    // Try to find a match by checking if path starts with any route
    for (const [route, title] of Object.entries(ROUTE_TITLES)) {
      if ((route.startsWith('/admin/') || route.startsWith('/onecare/admin/')) && 
          normalizedPath.startsWith(route)) {
        return title;
      }
    }
    // Format the path as a fallback
    const adminPath = normalizedPath.replace('/onecare/admin/', '').replace('/admin/', '');
    const formatted = adminPath.split('/').map(part => 
      part.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    ).join(' - ');
    return formatted ? `${formatted} - OneCare` : BRANDING.pageTitles.admin;
  }
  
  // Check for tools routes with parameters - handle both /tools/ and /onecare/tools/
  if (normalizedPath.startsWith('/tools/conductor/') || normalizedPath.startsWith('/onecare/tools/conductor/')) {
    return 'Conductor - OneCare';
  }
  if (normalizedPath.startsWith('/tools/smart-flow/') || normalizedPath.startsWith('/onecare/tools/smart-flow/')) {
    return 'Smart Flow - OneCare';
  }
  
  // Check for patient routes with parameters
  if (normalizedPath.startsWith('/onecare/patients/feedback/track/')) {
    return 'Track Feedback - OneCare';
  }
  
  // Default fallback
  return BRANDING.meta.defaultTitle;
};
